import { useState, useCallback } from "react";

/**
 * Manages client-side session file state for forms that upload files
 * alongside a larger entity save (e.g. a claim or cost entry).
 *
 * Files are uploaded to the backend immediately when added. The upload is
 * awaited so errors surface instantly while the user is still in the picker
 * dialog. Once the upload succeeds the session entry stores both the
 * client-generated UUID and the real server ID (Anlage.id).
 *
 * The RHF field value is the **server ID** — ready for submission without
 * any additional resolve step.
 *
 * At submission the caller should include both `id` (server) and `uuid`
 * (client) so the backend can verify ownership without relying on
 * sequential/guessable numeric IDs alone.
 *
 * Each session entry has the shape:
 *   {
 *     uuid:          string   — client-generated UUID (stable session key)
 *     id:            number   — real Anlage.id (set once upload succeeds)
 *     name:          string
 *     description:   string|null
 *     size:          number
 *     mailOptimizedSize: number|null — size of the mail_optimized conversion, once generated
 *     mimeType:      string
 *     isSession:     true     — distinguishes from existing/linked files
 *     previewUrl:    string|null
 *   }
 *
 * Usage:
 *   const { sessionFiles, addSessionFile, removeSessionFile, clearSessionFiles }
 *     = useSessionFiles();
 *
 *   // Bind the upload function once (e.g. in ClaimCostsEditor):
 *   const uploadFn = useCallback(
 *     (file, uuid, desc) => uploadClaimAttachment(claimId, file, uuid, desc),
 *     [claimId]
 *   );
 *
 *   // Pass to one or more FilePickerField instances:
 *   <FilePickerField
 *     sessionFiles={sessionFiles}
 *     onAddSessionFile={(file) => addSessionFile(file, uploadFn)}
 *     ...
 *   />
 */
export function useSessionFiles() {
  const [sessionFiles, setSessionFiles] = useState([]);

  /**
   * Upload a new File via uploadFn and register it as a session entry.
   *
   * @param {File} file
   * @param {(file: File, uuid: string, description?: string) => Promise<{ id: number|string }>} uploadFn
   * @param {string|null} description - Optional user-provided description/label
   * @returns {Promise<{ uuid: string, id: number|string }>} The client UUID and server ID.
   * @throws When the upload fails — caller should surface the error to the user.
   */
  const addSessionFile = useCallback(
    async (file, uploadFn, description = null) => {
      const uuid = crypto.randomUUID();

      const serverRecord = await uploadFn(file, uuid, description);

      const entry = {
        uuid,
        id: serverRecord.id,
        name: file.name,
        description,
        used_in: [],
        size: file.size,
        mailOptimizedSize: serverRecord.mailOptimizedSize ?? null,
        mimeType: file.type,
        isSession: true,
        previewUrl: file.type.startsWith("image/")
          ? URL.createObjectURL(file)
          : null,
      };

      setSessionFiles((prev) => [...prev, entry]);

      return { uuid, id: serverRecord.id };
    },
    []
  );

  /**
   * Remove a session file by its client UUID.
   * @param {string} uuid
   */
  const removeSessionFile = useCallback((uuid) => {
    setSessionFiles((prev) => {
      const entry = prev.find((f) => f.uuid === uuid);
      if (entry?.previewUrl) {
        URL.revokeObjectURL(entry.previewUrl);
      }
      return prev.filter((f) => f.uuid !== uuid);
    });
  }, []);

  /**
   * Update the description (bezeichnung) of a session file.
   * @param {string} uuid
   * @param {string|null} description
   */
  const updateSessionFileDescription = useCallback((uuid, description) => {
    setSessionFiles((prev) =>
      prev.map((sf) => (sf.uuid === uuid ? { ...sf, description } : sf))
    );
  }, []);

  const updateSessionFileUsedIn = useCallback((uuid, used_in) => {
    setSessionFiles((prev) =>
      prev.map((sf) => (sf.uuid === uuid ? { ...sf, used_in } : sf))
    );
  }, []);

  /**
   * Look up the client UUID for a given server ID.
   * @param {number|string} serverId
   * @returns {string|null}
   */
  const getUuidForServerId = useCallback(
    (serverId) => {
      const entry = sessionFiles.find(
        (sf) => Number(sf.id) === Number(serverId)
      );
      return entry?.uuid ?? null;
    },
    [sessionFiles]
  );

  /**
   * Clear all session entries (after successful form submission or modal close).
   */
  const clearSessionFiles = useCallback(() => {
    setSessionFiles((prev) => {
      prev.forEach((entry) => {
        if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
      });
      return [];
    });
  }, []);

  return {
    sessionFiles,
    addSessionFile,
    removeSessionFile,
    updateSessionFileDescription,
    updateSessionFileUsedIn,
    getUuidForServerId,
    clearSessionFiles,
  };
}
